import {ActivatedRouteSnapshot, ResolveFn} from '@angular/router';
import {map} from 'rxjs/operators';
import {FinancialHealthIndicatorRouteParameters} from '../financial-health-indicator-route-parameters';
import {inject} from '@angular/core';
import {DatasetService} from '../../../services/api/fit-api/dataset.service';
import {resolveGovernmentIdDataset} from '../../../services/resolvers/government-id-dataset.resolver';
import {of, switchMap} from 'rxjs';
import {FitApiService} from '../../../services/api/fit-api/fit-api.service';
import {MCAG} from '../../../services/api/fit-api/models/mcag';
import {Indicator} from '../../../services/api/fit-api/models/indicators/indicator';
import {EndYearRouteOption} from '../../../models/route-options/end-year-route-option';
import {StartYearRouteOption} from '../../../models/route-options/start-year-route-option';
import {DatasetDetail} from '../../../services/api/fit-api/models/datasets/dataset-type';

/**
 * Resolve indicator for indicator code
 * Flatten indicator codes resolved from route and find indicator using the indicator code route param
 *
 * @param route
 */
export const financialHealthIndicatorResolver: ResolveFn<Indicator | null> = (route) => {
	return resolveFinancialHealthIndicator(route);
};

export function resolveFinancialHealthIndicator(route: ActivatedRouteSnapshot) {
	const datasetService = inject(DatasetService);
	const fitApiService = inject(FitApiService);

	const mcag = route.paramMap.get(FinancialHealthIndicatorRouteParameters.mcag) as MCAG | null;
	const indicatorCode = route.paramMap.get(FinancialHealthIndicatorRouteParameters.indicatorCode);

	if (mcag === null || indicatorCode === null) {
		return of(null);
	}

	return resolveGovernmentIdDataset(route).pipe(
		switchMap(dataset => {
			if (dataset === null) {
				throw new Error("Dataset not found.  Should have been caught by the route guard(s).");
			}
			return datasetService.getDatasetDetail(dataset?.datasetType, dataset.id);
		}),
		switchMap(dd => {
			// determine start and end years with RouteOptions
			const startYear = getStartYearRouteOption(route, [dd]).appliedValue;
			const endYear = getEndYearRouteOption(route, [dd]).appliedValue;

			return fitApiService.getIndicators(dd, mcag, startYear, endYear, indicatorCode).pipe(
				map(indicators => indicators.length === 0 ? null : indicators[0])
			);
		})
	);
}

export function getEndYearRouteOption(route: ActivatedRouteSnapshot, datasetsWithDetail: DatasetDetail[]) {
	const [, endYear] = DatasetService.getDefaultYearRangeFromDatasets(datasetsWithDetail);

	return new EndYearRouteOption(
		route,
		new Set(datasetsWithDetail.map(x => x.detail.includedYears).flat().sort((a, b) => a - b)),
		endYear,
	);
}

export function getStartYearRouteOption(route: ActivatedRouteSnapshot, datasetsWithDetail: DatasetDetail[]) {
	const [startYear,] = DatasetService.getDefaultYearRangeFromDatasets(datasetsWithDetail);

	return new StartYearRouteOption(
		route,
		new Set(datasetsWithDetail.map(x => x.detail.includedYears).flat().sort((a, b) => a - b)),
		startYear,
	);
}
